import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import React from "react";
import styled from "styled-components";

import { useGetSimilarCorrectClaimsQuery } from "../../store/queries/claims";

interface ISimilarClaimsSectionProps {
  claimId: string;
  onNavigateToClaim?: (claimId: string) => void;
}

const SectionWrapper = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e9ecef;
`;

const SectionTitle = styled.h4`
  margin: 0 0 0.75rem 0;
  font-size: 14px;
  font-weight: 600;
  color: #495057;
`;

const SimilarClaimsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const SimilarClaimsTableHeader = styled.th`
  text-align: left;
  padding: 0.75rem;
  border-bottom: 2px solid #e9ecef;
  font-weight: 600;
  color: #495057;
  background-color: #fff;
`;

const SimilarClaimsTableRow = styled.tr<{ isClickable?: boolean }>`
  cursor: ${(props) => (props.isClickable ? "pointer" : "default")};

  &:hover {
    background-color: ${(props) => (props.isClickable ? "#e3f2fd" : "inherit")};
  }
`;

const SimilarClaimsTableCell = styled.td`
  padding: 0.75rem;
  border-bottom: 1px solid #e9ecef;
  color: #212529;
`;

const SimilarClaimsInfoText = styled.div`
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  background-color: #e8f5e9;
  border-left: 3px solid #4caf50;
  color: #2e7d32;
  font-size: 14px;
  line-height: 1.5;
`;

const InfoMessage = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 0.75rem 1rem;
  background-color: #e3f2fd;
  border-radius: 4px;
  color: #1565c0;
  font-size: 14px;
  line-height: 1.5;
`;

/**
 * Currency formatter for cents to dollars
 */
const formatCurrency = (cents: number | null): string => {
  if (cents === null || cents === undefined) return "-";
  const dollars = cents / 100;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(dollars);
};

/**
 * Component to display similar correctly-paid claims
 * Shows on the Claim Details tab when there are underpaid line items
 *
 * Note: This component no longer requires AI analysis. The backend calculates
 * underpaid line items directly from lineItems and precalculatedAllowable data.
 */
export function SimilarClaimsSection({
  claimId,
  onNavigateToClaim,
}: ISimilarClaimsSectionProps) {
  // Always fetch - backend determines applicability
  // Backend returns NOT_APPLICABLE (400) if no underpaid line items exist
  const {
    data: similarClaimsData,
    isLoading,
    isError,
    error,
  } = useGetSimilarCorrectClaimsQuery({ claimId });

  // Check if this is a NOT_APPLICABLE error (not a true error)
  const isNotApplicable =
    isError &&
    error &&
    "status" in error &&
    error.status === 400 &&
    "data" in error &&
    (error.data as { code?: string })?.code === "NOT_APPLICABLE";

  const handleRowClick = (targetClaimId: string) => {
    if (onNavigateToClaim) {
      onNavigateToClaim(targetClaimId);
    }
  };

  return (
    <SectionWrapper>
      <SectionTitle>Similar Correctly Paid Claims</SectionTitle>

      {isLoading && (
        <div style={{ textAlign: "center", padding: "1rem" }}>
          <ProgressSpinner style={{ width: "30px", height: "30px" }} />
        </div>
      )}

      {isNotApplicable && (
        <InfoMessage>
          <i className="pi pi-info-circle" style={{ marginRight: "0.5rem" }} />
          Similar claims comparison is available when the AI analysis identifies
          services that were paid below the expected amount.
        </InfoMessage>
      )}

      {isError && !isNotApplicable && (
        <Message
          severity="warn"
          text="Unable to load similar claims. Please try again later."
          style={{ width: "100%" }}
        />
      )}

      {!isLoading &&
        !isError &&
        similarClaimsData &&
        similarClaimsData.totalFound === 0 && (
          <Message
            severity="info"
            text="No similar correctly-paid claims found for this payer with matching CPT codes."
            style={{ width: "100%" }}
          />
        )}

      {!isLoading &&
        !isError &&
        similarClaimsData &&
        similarClaimsData.totalFound > 0 && (
          <>
            <SimilarClaimsInfoText>
              Found {similarClaimsData.totalFound} claim(s) with CPT codes{" "}
              {similarClaimsData.matchCriteria.problemCptCodes.join(", ")} from{" "}
              {similarClaimsData.matchCriteria.clearPayerName} that were paid
              correctly. {onNavigateToClaim && "Click a row to view details."}
            </SimilarClaimsInfoText>

            <SimilarClaimsTable>
              <thead>
                <tr>
                  <SimilarClaimsTableHeader>
                    Service Date
                  </SimilarClaimsTableHeader>
                  <SimilarClaimsTableHeader>Claim #</SimilarClaimsTableHeader>
                  <SimilarClaimsTableHeader>Patient</SimilarClaimsTableHeader>
                  <SimilarClaimsTableHeader>
                    Case Signature
                  </SimilarClaimsTableHeader>
                  <SimilarClaimsTableHeader>Paid</SimilarClaimsTableHeader>
                </tr>
              </thead>
              <tbody>
                {similarClaimsData.similarClaims.map((claim) => (
                  <SimilarClaimsTableRow
                    key={claim.id}
                    isClickable={!!onNavigateToClaim}
                    onClick={() => handleRowClick(claim.id)}
                  >
                    <SimilarClaimsTableCell>
                      {new Date(claim.serviceDate).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </SimilarClaimsTableCell>
                    <SimilarClaimsTableCell>
                      {claim.claimNumber || "-"}
                    </SimilarClaimsTableCell>
                    <SimilarClaimsTableCell data-pii="patient-name">
                      {claim.patientName}
                    </SimilarClaimsTableCell>
                    <SimilarClaimsTableCell>
                      {claim.caseSignature}
                    </SimilarClaimsTableCell>
                    <SimilarClaimsTableCell>
                      {formatCurrency(claim.claimAmount)}
                    </SimilarClaimsTableCell>
                  </SimilarClaimsTableRow>
                ))}
              </tbody>
            </SimilarClaimsTable>
          </>
        )}
    </SectionWrapper>
  );
}
